import { z } from 'zod';
import { IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common';

export const AI_PURPOSES = [
  'onboarding',
  'general_chat',
  'segmentation',
  'dashboard_codegen',
  'insights',
  'review_reply',
  'campaign_copy',
  'import_mapping',
  'email_design',
  /** Forms engine: AI page design over blocks (FORMS_DESIGN §6.5). */
  'form_design',
  /** Forms engine: grounded Q&A over a form's answers (FORMS_DESIGN §13). */
  'form_insights',
] as const;
export type AiPurpose = (typeof AI_PURPOSES)[number];
export const AiPurposeSchema = z.enum(AI_PURPOSES);

export const AI_PROVIDERS = ['anthropic', 'openai', 'fake'] as const;
export type AiProviderName = (typeof AI_PROVIDERS)[number];

/** Reasoning effort per model route (Anthropic `output_config.effort`); null = provider default. */
export const AI_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type AiEffort = (typeof AI_EFFORTS)[number];

/** Spec §8.3 */
export const AGENT_RUN_STATES = [
  'created',
  'queued',
  'running',
  'awaiting_approval',
  'completed',
  'failed',
  'cancelled',
  'timed_out',
] as const;
export type AgentRunState = (typeof AGENT_RUN_STATES)[number];

export const CONVERSATION_KINDS = ['chat', 'onboarding', 'dashboard', 'segment'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export const CONVERSATION_MESSAGE_ROLES = ['user', 'assistant', 'tool', 'system'] as const;
export type ConversationMessageRole = (typeof CONVERSATION_MESSAGE_ROLES)[number];

export const MEMORY_KINDS = ['preference', 'fact', 'style', 'goal', 'constraint'] as const;
export type MemoryKind = (typeof MEMORY_KINDS)[number];

export const MEMORY_STATUSES = ['active', 'disabled', 'archived'] as const;

export const NOTE_TYPES = [
  'general',
  'promotion',
  'staff',
  'operations',
  'event',
  'feedback',
  'goal',
] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

/**
 * `user_reported`: the user said it (server-checked against their own turns for chat notes);
 * `confirmed`: an owner/admin confirmed it; `ai_inferred`: saved by the assistant from text that
 * was not the user's own words — unverified, hidden from insights until confirmed.
 */
export const NOTE_VERIFICATIONS = ['user_reported', 'confirmed', 'ai_inferred'] as const;
export type NoteVerification = (typeof NOTE_VERIFICATIONS)[number];

export const ModelRouteSchema = z.object({
  purpose: AiPurposeSchema,
  provider: z.enum(AI_PROVIDERS),
  model: z.string().min(1),
  fallbacks: z.array(z.object({ provider: z.enum(AI_PROVIDERS), model: z.string() })).default([]),
  temperature: z.number().min(0).max(2).nullable(),
  effort: z.enum(AI_EFFORTS).nullable().default(null),
  maxOutputTokens: z.number().int().positive(),
  maxToolCalls: z.number().int().nonnegative(),
  creditBudget: z.number().int().nonnegative(),
  promptVersion: z.string(),
  enabled: z.boolean(),
  version: z.number().int(),
});
export type ModelRoute = z.infer<typeof ModelRouteSchema>;

// ─── runtime error categories (agent_runs.error_category) ───────────────────
export const AI_ERROR_CATEGORIES = [
  'insufficient_credits',
  'budget_exhausted',
  'route_disabled',
  'provider_unavailable',
  'provider_error',
  'rate_limited',
  'timeout',
  'cancelled',
  'invalid_output',
  'tool_error',
  'refusal',
  'internal',
] as const;
export type AiErrorCategory = (typeof AI_ERROR_CATEGORIES)[number];

/** Why a successful run stopped (agent_runs.output.stopReason). */
export const AI_STOP_REASONS = [
  'end_turn',
  'budget',
  'max_iterations',
  'max_tool_calls',
  'token_budget',
  'wall_clock',
  'max_tokens',
] as const;
export type AiStopReason = (typeof AI_STOP_REASONS)[number];

// ─── provenance shown in the UI (never invented: produced by executed tools only) ─
/** i18n key + params describing what a tool did (`assistant:activity.<key>`). */
export const ToolSummarySchema = z.object({
  key: z.string().max(80),
  params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
});
export type ToolSummary = z.infer<typeof ToolSummarySchema>;

export const CITATION_KINDS = ['schema', 'metric', 'query', 'note', 'memory', 'contact'] as const;
export const CitationSchema = z.object({
  kind: z.enum(CITATION_KINDS),
  /** Stable reference (metric key, note id, …). */
  ref: z.string().max(200),
  label: z.string().max(200),
  /** Tool that produced the evidence. */
  tool: z.string().max(80),
  /** Notes: user-reported vs confirmed. */
  verification: z.enum(NOTE_VERIFICATIONS).optional(),
  /** Short value for numeric evidence (e.g. a count). */
  value: z.union([z.string(), z.number()]).optional(),
});
export type Citation = z.infer<typeof CitationSchema>;

export const ARTIFACT_KINDS = ['segment_draft', 'campaign_draft', 'note'] as const;
export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];
export const ArtifactSchema = z.object({
  kind: z.enum(ARTIFACT_KINDS),
  id: z.string(),
  title: z.string().max(200),
  /** In-app link to open the draft (never an action that sends). */
  href: z.string().nullable(),
  /** Extra display data (channel, audience size…). */
  meta: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
});
export type Artifact = z.infer<typeof ArtifactSchema>;

/** Streamed agent events (spec §14). Only sanitized, authorized data is streamed. */
export const AgentStreamEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('run.started'),
    runId: z.string(),
    conversationId: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal('tool.started'),
    runId: z.string(),
    tool: z.string(),
    callId: z.string().optional(),
  }),
  z.object({
    type: z.literal('tool.completed'),
    runId: z.string(),
    tool: z.string(),
    ok: z.boolean(),
    callId: z.string().optional(),
    /** `denied` = not allowed for this purpose; `invalid_input`; `failed`. */
    error: z.enum(['denied', 'invalid_input', 'failed']).optional(),
    summary: ToolSummarySchema.optional(),
    citations: z.array(CitationSchema).optional(),
  }),
  z.object({ type: z.literal('assistant.delta'), runId: z.string(), text: z.string() }),
  z.object({
    type: z.literal('artifact.ready'),
    runId: z.string(),
    kind: z.string(),
    id: z.string(),
    title: z.string().optional(),
    href: z.string().nullable().optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
  }),
  z.object({
    type: z.literal('approval.required'),
    runId: z.string(),
    action: z.string(),
    payload: z.unknown(),
  }),
  z.object({
    type: z.literal('run.completed'),
    runId: z.string(),
    usageCredits: z.number(),
    messageId: z.string().optional(),
    stopReason: z.enum(AI_STOP_REASONS).optional(),
  }),
  z.object({
    type: z.literal('run.failed'),
    runId: z.string(),
    errorCategory: z.string(),
    message: z.string(),
    usageCredits: z.number().optional(),
  }),
  z.object({ type: z.literal('run.cancelled'), runId: z.string(), usageCredits: z.number() }),
]);
export type AgentStreamEvent = z.infer<typeof AgentStreamEventSchema>;
/** Persisted event as sent over SSE (`id:` = seq, resume with `?after=<seq>` or Last-Event-ID). */
export type AgentStreamEnvelope = AgentStreamEvent & { seq: number };

// ─── platform_config knobs (Super Admin editable; defaults in modules/ai/pricing.ts) ─
/**
 * `ai.creditPolicy`: credits = max(minimum(purpose), ceil(cost_units / unit)) where
 * cost_units = input·in + cached_read·cached + cache_write·in·cacheWriteMultiplier + output·out.
 * Rates are per token in micro-USD (Claude Sonnet: in 2, out 10), so cost_units is also the
 * provider cost estimate in micro-USD (and numerically equal to USD per 1M tokens).
 *
 * Optional long-prompt tier per model (`longContext`): a single model call whose prompt
 * (input + cache read + cache write tokens) is ABOVE `thresholdInputTokens` is priced entirely at
 * the tier's rates (cache write = tier input · cacheWriteMultiplier). Applied per call, never to
 * totals summed across the calls of a run (`callCostMicros` in `aiPricing.ts`).
 */
const RatesSchema = z.object({
  input: z.number().nonnegative(),
  output: z.number().nonnegative(),
  cachedInput: z.number().nonnegative(),
});
export const AiLongContextRatesSchema = RatesSchema.extend({
  thresholdInputTokens: z.number().int().positive(),
});
export type AiLongContextRates = z.infer<typeof AiLongContextRatesSchema>;
export const AiModelRatesSchema = RatesSchema.extend({
  longContext: AiLongContextRatesSchema.optional(),
});
export type AiModelRates = z.infer<typeof AiModelRatesSchema>;
export const AiCreditPolicySchema = z.object({
  unit: z.number().positive(),
  defaultRates: AiModelRatesSchema,
  modelRates: z.record(z.string(), AiModelRatesSchema).default({}),
  cacheWriteMultiplier: z.number().nonnegative().default(1.25),
  minimumByPurpose: z.record(z.string(), z.number().int().nonnegative()).default({}),
  defaultMinimum: z.number().int().nonnegative().default(1),
});
export type AiCreditPolicy = z.infer<typeof AiCreditPolicySchema>;

/** `ai.runtimeLimits`: bounded agent loop (spec §8.3). */
export const AiRuntimeLimitsSchema = z.object({
  maxIterations: z.number().int().min(1).max(50),
  wallClockMs: z
    .number()
    .int()
    .min(1000)
    .max(15 * 60_000),
  maxTotalTokens: z.number().int().min(1000),
  toolOutputMaxChars: z.number().int().min(500).max(100_000),
  historyMessages: z.number().int().min(0).max(100),
  maxConcurrentRunsPerTenant: z.number().int().min(1).max(50),
});
export type AiRuntimeLimits = z.infer<typeof AiRuntimeLimitsSchema>;

// ─── REST contracts (/api/v1/ai) ─────────────────────────────────────────────
export const ConversationSchema = z.object({
  id: UuidSchema,
  kind: z.enum(CONVERSATION_KINDS),
  title: z.string().nullable(),
  lastMessageAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  archived: z.boolean(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const MessagePartSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('activity'),
    tool: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
    summary: ToolSummarySchema.optional(),
  }),
  CitationSchema.extend({ type: z.literal('citation') }),
  ArtifactSchema.extend({ type: z.literal('artifact') }),
  z.object({
    type: z.literal('usage'),
    credits: z.number().int(),
    model: z.string().nullable(),
  }),
  z.object({
    type: z.literal('stop'),
    reason: z.union([z.enum(AI_STOP_REASONS), z.enum(AI_ERROR_CATEGORIES)]),
  }),
]);
export type MessagePart = z.infer<typeof MessagePartSchema>;

export const ConversationMessageSchema = z.object({
  id: UuidSchema,
  role: z.enum(CONVERSATION_MESSAGE_ROLES),
  content: z.string(),
  parts: z.array(MessagePartSchema),
  runId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const AgentRunSummarySchema = z.object({
  id: UuidSchema,
  purpose: AiPurposeSchema,
  state: z.enum(AGENT_RUN_STATES),
  conversationId: UuidSchema.nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  modelConfigVersion: z.number().int().nullable(),
  creditsReserved: z.number().int(),
  creditsCaptured: z.number().int(),
  toolCalls: z.number().int(),
  fallbackUsed: z.boolean(),
  errorCategory: z.string().nullable(),
  lastSeq: z.number().int(),
  startedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
});
export type AgentRunSummary = z.infer<typeof AgentRunSummarySchema>;

export const ConversationDetailSchema = z.object({
  conversation: ConversationSchema,
  messages: z.array(ConversationMessageSchema),
  /** A run still streaming for this conversation (reconnect with its events endpoint). */
  activeRun: AgentRunSummarySchema.nullable(),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const CreateConversationRequestSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
});
export const UpdateConversationRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(120).optional(),
    archived: z.boolean().optional(),
  })
  .refine((v) => v.title !== undefined || v.archived !== undefined, 'Nothing to update');

export const StartRunRequestSchema = z.object({
  conversationId: UuidSchema.optional(),
  message: z.string().trim().min(1).max(4000),
  /** Client-generated id; a retried POST returns the same run. */
  requestId: z.string().min(8).max(100).optional(),
});
export type StartRunRequest = z.infer<typeof StartRunRequestSchema>;

export const StartRunResponseSchema = z.object({
  runId: UuidSchema,
  conversationId: UuidSchema,
  userMessageId: UuidSchema.nullable(),
  state: z.enum(AGENT_RUN_STATES),
  replayed: z.boolean(),
});
export type StartRunResponse = z.infer<typeof StartRunResponseSchema>;

export const BusinessMemorySchema = z.object({
  id: UuidSchema,
  kind: z.enum(MEMORY_KINDS),
  content: z.string(),
  source: z.enum(['chat', 'onboarding', 'user', 'insights']),
  status: z.enum(MEMORY_STATUSES),
  pinned: z.boolean(),
  /** Set = a personal preference of that user (only they see it). */
  personal: z.boolean(),
  createdBy: z.string().nullable(),
  lastUsedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type BusinessMemory = z.infer<typeof BusinessMemorySchema>;

export const NOTE_SOURCES = ['manual', 'chat', 'onboarding', 'insights'] as const;
export type NoteSource = (typeof NOTE_SOURCES)[number];

export const BusinessNoteSchema = z.object({
  id: UuidSchema,
  noteType: z.enum(NOTE_TYPES),
  title: z.string().nullable(),
  body: z.string(),
  verification: z.enum(NOTE_VERIFICATIONS),
  visibleToInsights: z.boolean(),
  pinned: z.boolean(),
  occurredOn: IsoDateSchema.nullable(),
  source: z.enum(NOTE_SOURCES),
  conversationId: UuidSchema.nullable(),
  createdBy: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type BusinessNote = z.infer<typeof BusinessNoteSchema>;

export const MemoryOverviewSchema = z.object({
  memories: z.array(BusinessMemorySchema),
  notes: z.array(BusinessNoteSchema),
  settings: z.object({ saveNotesFromChat: z.boolean() }),
  /** Whether the caller may pin/edit others' entries (owner/admin). */
  canManage: z.boolean(),
});
export type MemoryOverview = z.infer<typeof MemoryOverviewSchema>;

export const CreateNoteRequestSchema = z.object({
  noteType: z.enum(NOTE_TYPES).default('general'),
  title: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().min(1).max(4000),
  occurredOn: IsoDateSchema.nullable().optional(),
  visibleToInsights: z.boolean().default(true),
});
export const UpdateNoteRequestSchema = z.object({
  noteType: z.enum(NOTE_TYPES).optional(),
  title: z.string().trim().max(200).nullable().optional(),
  body: z.string().trim().min(1).max(4000).optional(),
  occurredOn: IsoDateSchema.nullable().optional(),
  visibleToInsights: z.boolean().optional(),
  pinned: z.boolean().optional(),
  /** Owner/admin can mark a user-reported note as confirmed. */
  verification: z.enum(NOTE_VERIFICATIONS).optional(),
});
export const CreateMemoryRequestSchema = z.object({
  kind: z.enum(MEMORY_KINDS),
  content: z.string().trim().min(1).max(1000),
  pinned: z.boolean().default(false),
  personal: z.boolean().default(false),
});
export const UpdateMemoryRequestSchema = z.object({
  kind: z.enum(MEMORY_KINDS).optional(),
  content: z.string().trim().min(1).max(1000).optional(),
  pinned: z.boolean().optional(),
  status: z.enum(MEMORY_STATUSES).optional(),
});

export const AiSettingsSchema = z.object({ saveNotesFromChat: z.boolean() });
export type AiSettings = z.infer<typeof AiSettingsSchema>;

export const AiSuggestionsSchema = z.object({
  items: z.array(z.object({ key: z.string(), text: z.string() })),
});

export const AI_DRAFT_STATUSES = ['draft', 'opened', 'promoted', 'discarded'] as const;
export const AiDraftSchema = z.object({
  id: UuidSchema,
  kind: z.enum(['segment']),
  title: z.string(),
  status: z.enum(AI_DRAFT_STATUSES),
  payload: z.record(z.string(), z.unknown()),
  runId: UuidSchema.nullable(),
  conversationId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AiDraft = z.infer<typeof AiDraftSchema>;
